import { useCallback, useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import cn from "classnames";
import { api, FeatureRequestItem, FeatureRequestStatus, Roadmap, STATUS_META } from "./api";
import BoardLayout from "./BoardLayout";
import RequestCard from "./RequestCard";
import { useVoteToggle } from "./useVoteToggle";

const COLUMNS: { key: keyof Roadmap; status: FeatureRequestStatus; blurb: string }[] = [
  { key: "planned", status: "planned", blurb: "Committed to, not started." },
  { key: "in_progress", status: "in_progress", blurb: "Being built right now." },
  { key: "delivered", status: "delivered", blurb: "Recently shipped. See the changelog for everything." },
];

const RoadmapView: React.FC = () => {
  const [data, setData] = useState<Roadmap | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await api.roadmap());
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Failed to load the roadmap");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const { toggle, votingId } = useVoteToggle((id, patch) =>
    setData((prev) => {
      if (!prev) return prev;
      const map = (list: FeatureRequestItem[]) => list.map((i) => (i.id === id ? { ...i, ...patch } : i));
      return { planned: map(prev.planned), in_progress: map(prev.in_progress), delivered: map(prev.delivered) };
    })
  );

  return (
    <BoardLayout subtitle="What's committed, what's being built, and what just shipped.">
      {!data ? (
        <div className="animate-pulse grid grid-cols-1 md:grid-cols-3 gap-4 max-w-6xl mx-auto">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-64 rounded-lg bg-base-300" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start max-w-6xl mx-auto">
          {COLUMNS.map((col) => {
            const meta = STATUS_META[col.status];
            const list = data[col.key];
            return (
              <section key={col.key} className="rounded-lg border border-base-300 bg-base-200/40 p-3">
                <header className="flex items-center gap-2 px-1 pb-3">
                  <span className={cn("size-2.5 rounded-full", meta.dot)} />
                  <h2 className="font-semibold text-sm">{meta.label}</h2>
                  <span className="text-xs text-base-content/40 tabular-nums">{list.length}</span>
                </header>
                <p className="px-1 pb-3 text-xs text-base-content/50">{col.blurb}</p>
                {list.length === 0 ? (
                  <p className="px-1 py-6 text-center text-xs text-base-content/40">Nothing here yet</p>
                ) : (
                  <div className="space-y-2">
                    {list.map((item, i) => (
                      <RequestCard key={item.id} item={item} index={i} compact onVote={toggle} voting={votingId === item.id} />
                    ))}
                  </div>
                )}
              </section>
            );
          })}
        </div>
      )}
    </BoardLayout>
  );
};

export default RoadmapView;
